// src/app/nav-footer-slot.tsx
// footerSlot composition for PractitionerNav: the account trigger (DS RailAction carrying a
// DS Avatar with the user's initials) and the account actions (name / role / practice /
// Account settings / Privacy / Terms / Log out) — faithful to the Flutter profile popup
// (menu_widget.dart onClickMenu: Settings / Privacy / Terms / Logout; "Profile" is a
// mobile-only no-op so it's dropped on web).
//
// Every rendered element here is a Signal DS primitive, chrome included. The actions open
// in a DS `AnchoredPopover` anchored beside the tile, level with it and clear of the rail — the DS published that surface, so the
// hand-rolled `position:absolute` menu this file used to carry is gone, and so is the
// stand-in `Modal` that briefly replaced it. `RailAction` is already a button, so it is NOT
// rendered THROUGH `AnchoredPopoverTrigger`'s `render` prop, so @base-ui owns the open and
// close transitions rather than this file toggling state. Doing it the other way — a
// controlled `open` plus `onClick={() => setOpen(v => !v)}` — measurably broke: @base-ui's
// own dismissal fired `onOpenChange(false)` and the click handler immediately flipped it
// back, so a second click on the tile never closed the menu and the exit transition never
// ran. `render` replaces the trigger element rather than nesting inside it, so there is
// still exactly one button.
//
// The tile passes `hideCaption`, as do the bell and Help tiles beside it: the footer cluster
// is glyph-only (Ahmed, 2026-09-04), so `label` serves only as the accessible name. The
// captions stay on the nav tiles above, which are destinations rather than utility actions.
//
// Avatar is 32px, round (design log 12 Oct, "rails made identical": the practice rail's 32px
// avatar, 12px from the rail's edge, centred in the 40px tile). It was `md` (40px) from the Figma
// rail. The menu (showcase, design log "profile menu identical"): right of the rail, bottom-aligned
// with the avatar, 256px, name over role, one divider, sentence-case 13px items, Log out last, no
// zoom — looks under `.rail-account-menu` in src/styles/showcase-bridge.css.
//
// This file owns data and wiring only: the auth store, navigation, and the legal URLs.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverDescription,
  AnchoredPopoverHeader,
  AnchoredPopoverItem,
  AnchoredPopoverSeparator,
  AnchoredPopoverTitle,
  AnchoredPopoverTrigger,
  Avatar,
  RailAction,
} from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { roleLabelFor } from '@/auth/use-role';
import { LEGAL_URLS } from '@/config/external-urls';
import { useRailExpanded } from './rail-state';
import {
  RAIL_POPOVER_ALIGN,
  RAIL_POPOVER_SIDE,
  RAIL_POPOVER_SIDE_OFFSET,
} from './rail-popover-anchoring';

function initials(firstName?: string, lastName?: string): string {
  const f = firstName?.[0] ?? '';
  const l = lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

export function NavFooterSlot() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [railOpen] = useRailExpanded();
  const anchorHostRef = useRef<HTMLDivElement>(null);

  const initStr = initials(user?.firstName, user?.lastName);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';
  const roleLabel = roleLabelFor(user);
  const practiceName = user?.practiceName;
  const description = [roleLabel, practiceName].filter(Boolean).join(' · ');

  function go(action: () => void) {
    setOpen(false);
    action();
  }
  const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    // `position: relative` is layout plumbing, not styling: it is the positioned ancestor
    // the in-place surface resolves against — the same mechanism the hand-rolled menu used
    // (`position:absolute; bottom:calc(100% + 8px); left:0`). Portalling to <body> instead
    // puts the offset parent a whole page away from the anchor, and under this app's root
    // `zoom: 0.9` the written offset is re-scaled, landing the surface ~90px high. Measured,
    // and the reason the app's own popovers are already misplaced.
    <div ref={anchorHostRef} className="relative">
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        <AnchoredPopoverTrigger
          render={
            <RailAction
              label="Account menu"
              hideCaption
              glyph={
                // With the rail open (232px) the name sits beside the disc, over the role; folded,
                // the text is not rendered and the disc stays where it was.
                <span className="rail-account">
                  <Avatar src={user?.imageUrl} initials={initStr} name={displayName} size={32} alt="" />
                  {railOpen && (
                    <span className="rail-account-text">
                      <span className="rail-account-name">{displayName}</span>
                      {roleLabel && <span className="rail-account-role">{roleLabel}</span>}
                    </span>
                  )}
                </span>
              }
            />
          }
        />

        <AnchoredPopoverContent
          container={anchorHostRef}
          side={RAIL_POPOVER_SIDE}
          align={RAIL_POPOVER_ALIGN}
          sideOffset={RAIL_POPOVER_SIDE_OFFSET}
          className="rail-account-menu"
        >
          <AnchoredPopoverHeader>
            <AnchoredPopoverTitle>{displayName}</AnchoredPopoverTitle>
            {description && (
              <AnchoredPopoverDescription>{description}</AnchoredPopoverDescription>
            )}
          </AnchoredPopoverHeader>
          <AnchoredPopoverSeparator />
          <AnchoredPopoverItem onClick={() => go(() => navigate('/settings'))}>
            Account settings
          </AnchoredPopoverItem>
          <AnchoredPopoverItem onClick={() => go(() => openExternal(LEGAL_URLS.privacyPolicy))}>
            Privacy policy
          </AnchoredPopoverItem>
          <AnchoredPopoverItem onClick={() => go(() => openExternal(LEGAL_URLS.termsOfUse))}>
            Terms of use
          </AnchoredPopoverItem>
          <AnchoredPopoverSeparator />
          <AnchoredPopoverItem onClick={() => go(logout)}>Log out</AnchoredPopoverItem>
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </div>
  );
}
